import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Plus, Minus, ArrowLeft, ArrowRight, Check, ShoppingCart, Calculator,
  Trash2, MapPin, ExternalLink, Package, X,
} from 'lucide-react';
import {
  FLOOR_PREP_PRODUCTS,
  EPOXY_PRIMERS,
  HIGH_BUILD_EPOXY,
  FLAKE_PRODUCTS,
  POLYASPARTIC_SEAL,
  HIGH_BUILD_SYSTEMS,
  FLAKE_COVERAGE,
  HIGH_BUILD_COLOUR_CHART,
} from '@/components/proepoxy/productData';
import { calculateOptimalPacks } from '@/components/proepoxy/utils';
import { submitProEpoxyOrder } from '@/components/proepoxy/submitOrder';
import PrioritySelector from '@/components/PrioritySelector';

export default function HighBuildGuidedStep2({ state, priority, setPriority, currentStep, goToStep, highBuildOrderMode, setHighBuildOrderMode, navigate, onExit }) {
  const {
    highBuildAreas, manualOrder,
    showInstallationGuide, setShowInstallationGuide,
    showColourChart, setShowColourChart,
    highBuildSteps,
    addHighBuildArea, removeHighBuildArea, updateHighBuildArea,
    initializeHighBuildProducts,
    calculateHighBuildTotal, getHighBuildItemCount,
    addPrimerItem, updatePrimerItem, removePrimerItem,
    addHighBuildItem, updateHighBuildItem, removeHighBuildItem,
    addFlakeItem, updateFlakeItem, removeFlakeItem,
    updateManualOrder, setManualOrder,
    calculateManualOrderTotal, getManualOrderItemCount,
    updateManualFloorPrepQty,
  } = state;
  return (
              <div className="space-y-6">
                <div className="bg-white rounded-2xl shadow-lg p-6">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-xl font-bold text-[#27292E]">Floor Preparation</h2>
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Calculator className="w-4 h-4" />
                      Areas from Step 1
                    </div>
                  </div>
                  <p className="text-gray-600 mb-6">Select self-smoothing compounds for each area as needed.</p>

                  <div className="space-y-6">
                    {highBuildAreas.map((area, index) => (
                      <div
                        key={area.id}
                        className="border-2 border-gray-200 rounded-xl p-4 hover:border-[#7A0B7A]/30 transition-colors"
                      >
                        {/* Area Header */}
                        <div className="flex items-center justify-between mb-4 pb-3 border-b border-gray-100">
                          <div>
                            <h3 className="font-bold text-[#27292E]">{area.name}</h3>
                            <p className="text-sm text-gray-500">{area.m2}m²</p>
                          </div>
                          <span className="px-3 py-1 bg-[#7A0B7A]/10 text-[#7A0B7A] rounded-full text-sm font-medium">
                            {area.m2}m²
                          </span>
                        </div>

                        {/* Self Smoothing Compounds Question */}
                        <div className="mb-4">
                          <h4 className="font-medium text-[#27292E] mb-3">Do you need self smoothing compounds for this area?</h4>
                          
                          <div className="flex gap-3 mb-3">
                            <button
                              onClick={() => updateHighBuildArea(area.id, 'needsSmoothingCompounds', true)}
                              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                                area.needsSmoothingCompounds === true
                                  ? 'bg-[#7A0B7A] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => updateHighBuildArea(area.id, 'needsSmoothingCompounds', false)}
                              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                                area.needsSmoothingCompounds === false
                                  ? 'bg-[#7A0B7A] text-white'
                                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              No
                            </button>
                          </div>

                          {/* Coverage Calculator */}
                          {area.needsSmoothingCompounds && (
                            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-4">
                              <p className="text-sm text-blue-800 font-medium mb-3">Coverage Calculator for {area.name}</p>
                              
                              {/* Depth Selector */}
                              <div className="mb-4">
                                <div className="flex items-center justify-between mb-2">
                                  <label className="text-sm text-blue-700">Select Depth:</label>
                                  <span className="text-lg font-bold text-blue-800">{area.smoothingDepth || 3}mm</span>
                                </div>
                                <input
                                  type="range"
                                  min="3"
                                  max="10"
                                  step="1"
                                  value={area.smoothingDepth || 3}
                                  onChange={(e) => updateHighBuildArea(area.id, 'smoothingDepth', parseFloat(e.target.value))}
                                  className="w-full h-2 bg-blue-200 rounded-lg appearance-none cursor-pointer accent-[#7A0B7A]"
                                />
                                <div className="flex justify-between text-xs text-blue-600 mt-1">
                                  <span>3</span><span>4</span><span>5</span><span>6</span><span>7</span><span>8</span><span>9</span><span>10</span>
                                </div>
                                <p className="text-center text-xs text-blue-500 mt-1">mm</p>
                              </div>

                              {/* Bag Calculation */}
                              {(() => {
                                const m2 = parseInt(area.m2) || 0;
                                const depth = area.smoothingDepth || 3;
                                const coveragePerBag = 4.5 * (3 / depth);
                                const bagsNeeded = Math.ceil(m2 / coveragePerBag);
                                
                                return (
                                  <div className="bg-white rounded-lg p-3 border border-blue-200">
                                    <div className="flex items-center justify-between">
                                      <div>
                                        <p className="text-sm text-gray-600">
                                          For <span className="font-bold text-[#7A0B7A]">{m2}m²</span> at <span className="font-bold text-[#7A0B7A]">{depth}mm</span> depth
                                        </p>
                                        <p className="text-xs text-gray-500">Coverage: {coveragePerBag.toFixed(2)}m² per bag</p>
                                      </div>
                                      <div className="text-right">
                                        <p className="text-2xl font-bold text-[#7A0B7A]">{bagsNeeded}</p>
                                        <p className="text-xs text-gray-500">bags needed</p>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                              <p className="text-xs text-blue-600 mt-2">Reference: 1 bag per 4.5m² at 3mm • 1 bag per 2.25m² at 6mm</p>
                            </div>
                          )}

                          {/* Smoothing Compound Selection */}
                          {area.needsSmoothingCompounds && (
                            <div className="space-y-3">
                              {FLOOR_PREP_PRODUCTS.smoothing_compounds.map(product => {
                                const qty = area.smoothingCompounds?.[product.id] || 0;
                                return (
                                  <div key={product.id} className={`p-3 rounded-xl border-2 transition-all ${qty > 0 ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200'}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-[#27292E] text-sm">{product.name}</h4>
                                        <p className="text-xs text-gray-500">{product.type}</p>
                                      </div>
                                      <div className="flex items-center justify-between sm:justify-end gap-3">
                                        <div className="text-left sm:text-right">
                                          <p className="font-bold text-[#7A0B7A] text-sm">£{product.price.toFixed(2)}</p>
                                          <p className="text-xs text-gray-400">per {product.unit}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => {
                                              const newQty = Math.max(0, qty - 1);
                                              updateHighBuildArea(area.id, 'smoothingCompounds', { ...area.smoothingCompounds, [product.id]: newQty });
                                            }}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                          >
                                            <Minus className="w-4 h-4" />
                                          </button>
                                          <span className="w-8 text-center font-bold">{qty}</span>
                                          <button
                                            onClick={() => {
                                              updateHighBuildArea(area.id, 'smoothingCompounds', { ...area.smoothingCompounds, [product.id]: qty + 1 });
                                            }}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                          >
                                            <Plus className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    {qty > 0 && (
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                                        <span className="text-gray-600">{qty} × £{product.price.toFixed(2)}</span>
                                        <span className="font-bold text-[#7A0B7A]">£{(qty * product.price).toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>

                        {/* Sub Floor Primers */}
                        <div>
                          <h4 className="font-medium text-[#27292E] mb-3">Do you need sub floor primers for this area?</h4>
                          <div className="flex gap-3 mb-3">
                            <button
                              onClick={() => updateHighBuildArea(area.id, 'needsSubFloorPrimer', true)}
                              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                                area.needsSubFloorPrimer === true ? 'bg-[#7A0B7A] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              Yes
                            </button>
                            <button
                              onClick={() => updateHighBuildArea(area.id, 'needsSubFloorPrimer', false)}
                              className={`flex-1 py-2 px-4 rounded-xl font-medium transition-colors ${
                                area.needsSubFloorPrimer === false ? 'bg-[#7A0B7A] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                              }`}
                            >
                              No
                            </button>
                          </div>

                          {area.needsSubFloorPrimer && (
                            <div className="space-y-3">
                              {FLOOR_PREP_PRODUCTS.primers.map(product => {
                                const qty = area.subFloorPrimers?.[product.id] || 0;
                                return (
                                  <div key={product.id} className={`p-3 rounded-xl border-2 transition-all ${qty > 0 ? 'border-[#7A0B7A] bg-[#7A0B7A]/5' : 'border-gray-200'}`}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                      <div className="flex-1 min-w-0">
                                        <h4 className="font-medium text-[#27292E] text-sm">{product.name}</h4>
                                        <p className="text-xs text-gray-500">{product.coverage || product.size}</p>
                                      </div>
                                      <div className="flex items-center justify-between sm:justify-end gap-3">
                                        <div className="text-left sm:text-right">
                                          <p className="font-bold text-[#7A0B7A] text-sm">£{product.price.toFixed(2)}</p>
                                          <p className="text-xs text-gray-400">per {product.unit}</p>
                                        </div>
                                        <div className="flex items-center gap-1">
                                          <button
                                            onClick={() => {
                                              const newQty = Math.max(0, qty - 1);
                                              updateHighBuildArea(area.id, 'subFloorPrimers', { ...area.subFloorPrimers, [product.id]: newQty });
                                            }}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg border-2 border-[#7A0B7A] text-[#7A0B7A]"
                                          >
                                            <Minus className="w-4 h-4" />
                                          </button>
                                          <span className="w-8 text-center font-bold">{qty}</span>
                                          <button
                                            onClick={() => {
                                              updateHighBuildArea(area.id, 'subFloorPrimers', { ...area.subFloorPrimers, [product.id]: qty + 1 });
                                            }}
                                            className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#7A0B7A] text-white"
                                          >
                                            <Plus className="w-4 h-4" />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                    {qty > 0 && (
                                      <div className="mt-2 pt-2 border-t border-gray-200 flex justify-between text-sm">
                                        <span className="text-gray-600">{qty} × £{product.price.toFixed(2)}</span>
                                        <span className="font-bold text-[#7A0B7A]">£{(qty * product.price).toFixed(2)}</span>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Running Total */}
                <div className="bg-[#7A0B7A] text-white rounded-2xl p-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">Order Total</p>
                      <p className="text-xs text-white/60">{getHighBuildItemCount()} item{getHighBuildItemCount() !== 1 ? 's' : ''} • Excl. VAT</p>
                    </div>
                    <p className="text-3xl font-bold">£{calculateHighBuildTotal().toFixed(2)}</p>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-4">
                  <Button onClick={() => goToStep(1)} variant="outline" className="flex-1 h-14 text-lg font-medium rounded-xl border-2 border-[#7A0B7A] text-[#7A0B7A] hover:bg-[#7A0B7A]/5">
                    <ArrowLeft className="w-5 h-5 mr-2" />Back
                  </Button>
                  <Button onClick={() => goToStep(3)} className="flex-1 bg-[#7A0B7A] hover:bg-[#5a085a] text-white h-14 text-lg font-bold rounded-xl">
                    Continue<ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </div>
              </div>
  );
}
